// aiChatFunctions
export default [
    {
        name: "select_element",
        description:
            "Find/select an element based on their element_id. You can deduce the id based on extra information provided in the list of layers in the template. If you can't deduce, use the currently selected layer.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be selected.",
                },
            },
            required: ["element_id"],
        },
    },
    {
        name: "open_product_selector",
        description:
            "Suggest opening the product selector popup so the user can browse and choose a product image. Use this when the user wants to replace a product with a REAL product (e.g., 'replace with a Pepsi can', 'use a different product image', 'change the product'). This will ask the user for confirmation before opening. Do NOT use select_element for product replacements - use this function instead.",
        parameters: {
            type: "object",
            properties: {
                dummy: {
                    type: "string",
                    description: "Not used.",
                },
            },
            required: [],
        },
    },
    {
        name: "add_element_product_id",
        description:
            "Add a new product element to the template based on its ID, GTIN, or ASIN. This function creates a new element rather than modifying an existing one.",
        parameters: {
            type: "object",
            properties: {
                product_id: {
                    type: "string",
                    description: "The product ID, GTIN or ASIN code of the product to add.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Whether to add the product to all templates. Defaults to false unless requested by the user.",
                },
            },
            required: ["product_id", "apply_to_all"],
        },
    },
    {
        name: "change_element_product_id",
        description:
            "Edit/change/replace product IDs, GTIN, ASIN, or search by filename of the currently selected element. Use this when the user wants to replace an existing image on the canvas with a specific product or file. The search term can be a product ID, GTIN, ASIN, or a filename (e.g., 'doritos1' for 'doritos1.png').",
        parameters: {
            type: "object",
            properties: {
                new_product_id: {
                    type: "string",
                    description: "The new ID, GTIN or ASIN code.",
                },
                apply_to: {
                    type: "string",
                    enum: [
                        "replace_selected_product_image",
                        "replace_all_product_images",
                        "replace_visible_product_images",
                        "apply_to_all_templates"
                    ],
                    description: "Where to apply the replacement. Default is 'replace_selected_product_image'.",
                },
            },
            required: ["new_product_id", "apply_to"],
        },
    },
    {
        name: "replace_element_image",
        description:
            "Replace the image of an element based on their element_id. You can deduce the id based on extra information provided in the list of layers in the template. If you can't deduce, use the currently selected layer.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be selected. Optional - if not provided, will use the currently selected element.",
                },
                description_of_the_new_image: {
                    type: "string",
                    description: "A description of the new image that will be generated with AI, and replace the old image.",
                },
                auto_apply: {
                    type: "string",
                    enum: ["replace", "new_template"],
                    description: "Optional. If the user explicitly says 'replace the current image' or 'add to a new template' in their prompt, set this to skip the confirmation widgets and apply automatically. Only set this if the user clearly stated their preference.",
                },
            },
            required: ["description_of_the_new_image"],
        },
    },
    {
        name: "edit_element_image",
        description:
            "Edit the content or appearance of an image element. This function should be used when the user wants to modify the actual image content (like removing text from an image, changing colors, adding/removing elements, etc.) rather than editing text elements. If the user wants to edit text that exists as a separate text element, use edit_element_text instead. You can deduce the id based on extra information provided in the list of layers in the template. If you can't deduce, use the currently selected layer. Supports an optional reference image from another template to guide the edit.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the image element that is going to be edited. Optional - if not provided, will use the currently selected element.",
                },
                changes_to_the_image: {
                    type: "string",
                    description: "A description of the changes that will be made to the image content (e.g., 'remove the text NO ARTIFICIAL FLAVORS', 'make the background white', 'remove the person from the image').",
                },
                reference_template_index: {
                    type: "integer",
                    description: "Optional. 1-based index of another template that contains a reference image to guide the edit (e.g., 'use the image from template 2 as reference'). Use when the user references an image from a different template.",
                },
                reference_element_id: {
                    type: "string",
                    description: "Optional. Element ID of the reference image in the reference template. If not provided but reference_template_index is set, the first image in that template will be used.",
                },
                auto_apply: {
                    type: "string",
                    enum: ["replace", "new_template"],
                    description: "Optional. If the user explicitly says 'replace the current image' or 'add to a new template' in their prompt, set this to skip the confirmation widgets and apply automatically. Only set this if the user clearly stated their preference.",
                },
            },
            required: ["changes_to_the_image"],
        },
    },
    {
        name: "edit_element_text",
        description:
            "Edit the text of one or more elements. Use for any text update — single or multiple fields at once. Each update specifies a new_text and an optional element_id (deduced from layers; omit to use the currently selected element).",
        parameters: {
            type: "object",
            properties: {
                updates: {
                    type: "array",
                    description: "List of text updates. Use a single-item array for one element.",
                    items: {
                        type: "object",
                        properties: {
                            element_id: {
                                type: "string",
                                description: "Id of the element to update. Optional — omit to use the currently selected element.",
                            },
                            new_text: {
                                type: "string",
                                description: "New text value.",
                            },
                        },
                        required: ["new_text"],
                    },
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply each change to all templates. It defaults to false unless requested by the user.",
                },
            },
            required: ["updates", "apply_to_all"],
        },
    },
    {
        name: "edit_element_color",
        description:
            "Edit the color of an element based on their element_id. You can deduce the id based on extra information provided in the list of layers in the template. If you can't deduce, use the currently selected layer.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be edited. Optional - if not provided, will use the currently selected element.",
                },
                new_color: {
                    type: "string",
                    description: "New color of the element in hex format.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the new color to all templates. It defaults to false unless requested by the user.",
                }
            },
            required: ["new_color", "apply_to_all"],
        },
    },
    {
        name: "edit_element_font_size",
        description:
            "Edit the font size of an element based on their element_id. You can deduce the id based on extra information provided in the list of layers in the template. If you can't deduce, use the currently selected layer.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be edited. Optional - if not provided, will use the currently selected element.",
                },
                new_font_size: {
                    type: "string",
                    description: "New font size of the element.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the new font size to all templates. It defaults to false unless requested by the user.",
                }
            },
            required: ["new_font_size", "apply_to_all"],
        },
    },
    {
        name: "edit_element_font_family",
        description:
            "Edit the font family of an element based on their element_id. You can deduce the id based on extra information provided in the list of layers in the template If you can't deduce, use the currently selected layer. Wathever font the user specifies should be accepted.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be edited. Optional - if not provided, will use the currently selected element.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the new font family to all templates. It defaults to false unless requested by the user.",
                }
            },
            required: ["apply_to_all"],
        },
    },
    {
        name: "edit_project_name",
        description: "Edit the project name of a template. Targets the selected template by default, or a specific template via template_index (1-based ordinal: 1 = first, 2 = second, etc., also matches 'Template #2', 'Template 2').",
        parameters: {
            type: "object",
            properties: {
                new_project_name: {
                    type: "string",
                    description: "New project name of the template.",
                },
                template_index: {
                    type: "integer",
                    description: "Optional 1-based index of the template to rename (1 = first, 2 = second, etc.). Defaults to the currently selected template.",
                },
            },
            required: ["new_project_name"],
        },
    },
    {
        name: "generate_proof_link",
        description: "Generate a proof link for the current project to share with others.",
    },
    {
        name: "generate_image",
        description: "Generate an image generation prompt based on the user's request.",
        parameters: {
            type: "object",
            properties: {
                model: {
                    type: "string",
                    description: "The image generation model (also known as image generation function). If not specified, use 'gemini_nano_banana_2_image_generate' by default.",
                    enum: [
                        "gpt_image_1_generate",
                        "gemini_2_5_flash_image_generate",
                        "gemini_nano_banana_pro_image_generate",
                        "gemini_nano_banana_2_image_generate"
                    ]
                },
                prompt: {
                    type: "string",
                    description: "The image generation prompt in natural language.",
                },
                number_of_images: {
                    type: "integer",
                    minimum: 1,
                    maximum: 4,
                    description: "The number of images to generate. It defaults to 1 unless requested by the user.",
                },
                add_to_new_template_of_size: {
                    description: "In case the user wants to add the generated image(s) to a new template, the user can set the width and height of the new template. If the user didn't ask to add as a new template, this parameter should be null.",
                    oneOf: [
                        {
                            type: "null"
                        },
                        {
                            type: "object",
                            additionalProperties: false,
                            properties: {
                            width: {
                                type: "integer",
                                description: "Width of the new template."
                            },
                            height: {
                                type: "integer",
                                description: "Height of the new template."
                            }
                            },
                            required: ["width", "height"]
                        }
                    ]
                }
            },
            required: ["model", "prompt", "number_of_images", "add_to_new_template_of_size"],
        },
    },
    {
        name: "check_product_id",
        description: "Use this function only when the user wants to verify if one or multiple product IDs, GTINs, or ASINs exist in the database. It should not be used as part of replacement or image editing flows.",
        parameters: {
            type: "object",
            properties: {
                product_id: {
                    type: "string",
                    description: "One or more product IDs, GTINs, or ASINs to check. For multiple IDs, separate them with spaces.",
                },
            },
            required: ["product_id"],
        }
    },
    {
        name: "publish_to_platform",
        description: "Open the publish dialog and select the specified platform",
        parameters: {
            type: "object",
            properties: {
                platform_type: {
                    type: "string",
                    enum: ['bynder', 'salsify', 'walmart'],
                    description: "Type of platform to publish to (Bynder, Salsify, or Walmart)"
                }
            },
            required: ['platform_type']
        },
        function: function(args) {
            const platformType = args.platform_type?.toLowerCase();
            if (!['bynder', 'salsify', 'walmart'].includes(platformType)) {
                return { error: `Unsupported platform type: ${platformType}` };
            }
            return { platform_type: platformType };
        }
    },
    {
        name: "move_object",
        description: "Move any object (image, text, or shape) in one of the four directions (up, down, left, right). ONLY use this for relative movements when the user explicitly says directional terms like 'move it left', 'move up', 'shift down', etc. DO NOT use this function when the user mentions 'x' or 'y' with numbers (like 'set x to 100' or 'y=200') - those ALWAYS refer to absolute coordinates and should use position_object instead. This function is specifically for relative directional movements, not setting exact coordinates. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description: "Id of the element that is going to be moved. Optional - if not provided, will use the currently selected element.",
                },
                direction: {
                    type: "string",
                    enum: ["up", "down", "left", "right"],
                    description: "Direction to move the element."
                },
                distance: {
                    type: "number",
                    description: "Distance to move the element in pixels. Default is 10 if not specified.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the movement to all templates. Defaults to false unless requested by the user. Only applies to text and shape elements, not images.",
                }
            },
            required: ["direction"],
        },
    },
    {
        name: "rotate_image",
        description: "Rotate an image element by a specified angle. Works for product images and background theme images. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description: "Id of the image element that is going to be rotated. Optional - if not provided, will use the currently selected element.",
                },
                angle: {
                    type: "number",
                    description: "Angle in degrees to rotate the image. Use 90 for 90 degrees clockwise, -90 for 90 degrees counterclockwise, etc."
                }
            },
            required: ["angle"]
        }
    },
    {
        name: "remove_image_background",
        description: "Remove the background from an image element. Works for product images and background theme images. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description: "Id of the image element to remove background from. Optional - if not provided, will use the currently selected element.",
                },
                crop_to_image: {
                    type: "boolean",
                    description: "Whether to crop to the image after removing the background."
                }
            },
            required: []
        }
    },
    {
        name: "align_text",
        description: "Change the text alignment of a text element. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description: "Id of the text element to align. Optional - if not provided, will use the currently selected element.",
                },
                alignment: {
                    type: "string",
                    enum: ["left", "center", "right"],
                    description: "Text alignment to apply."
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the alignment to all templates. Defaults to false unless requested by the user."
                }
            },
            required: ["alignment", "apply_to_all"]
        }
    },
    {
        name: "edit_shape_color",
        description:
            "Change the background/fill color of a shape element. If no element_id is specified, the system will try to find a shape based on the context of the user's message.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be edited. Optional - if not provided, will attempt to find a shape by context.",
                },
                new_color: {
                    type: "string",
                    description: "New color of the element in hex format.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the new color to all templates. It defaults to false unless requested by the user.",
                }
            },
            required: ["new_color", "apply_to_all"],
        },
    },
    {
        name: "edit_shape_gradient",
        description: "Apply a gradient fill to a shape element. Use when the user asks for a gradient background, gradient fill, or mentions two colors transitioning (e.g., 'gradient from #FF0000 to #0000FF'). If no element_id is specified, the system will try to find a shape based on context.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description: "Id of the shape element. Optional - if not provided, will attempt to find a shape by context.",
                },
                gradient_type: {
                    type: "string",
                    enum: ["linear", "radial"],
                    description: "Type of gradient. Default: linear.",
                },
                direction: {
                    type: "string",
                    enum: ["to right", "to left", "to bottom", "to top"],
                    description: "Direction of linear gradient. 'to right' means left color transitions to right color. Default: 'to right'.",
                },
                color_stops: {
                    type: "array",
                    items: {
                        type: "object",
                        properties: {
                            offset: { type: "number", description: "Position from 0 to 100." },
                            color: { type: "string", description: "Color in hex format." }
                        },
                        required: ["offset", "color"]
                    },
                    description: "Array of color stops. Minimum 2 stops. Example: [{offset: 0, color: '#FF0000'}, {offset: 100, color: '#0000FF'}]"
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply to all templates. Defaults to false.",
                }
            },
            required: ["color_stops"],
        },
    },
    {
        name: "edit_shape_border",
        description:
            "Edit the border of a shape based on their element_id. If no element_id is specified, the system will try to find a shape based on the context of the user's message.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be edited. Optional - if not provided, will attempt to find a shape by context.",
                },
                border_color: {
                    type: "string",
                    description: "New border color in hex format.",
                },
                border_width: {
                    type: "number",
                    description: "New border width/thickness in pixels.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the changes to all templates. It defaults to false unless requested by the user.",
                }
            },
            required: ["apply_to_all"],
        },
    },
    {
        name: "center_object",
        description:
            "Center the selected object in the canvas. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element that is going to be centered. Optional - if not provided, will use the currently selected element.",
                },
                alignment: {
                    type: "string",
                    enum: ["both", "horizontal", "vertical"],
                    description: "How to center the object: horizontally, vertically, or both (center in canvas).",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the centering to all templates. It defaults to false unless requested by the user.",
                }
            },
            required: ["alignment", "apply_to_all"],
        },
    },
    {
        name: "change_layer_order",
        description:
            "Change the order of a layer by bringing it to front, sending to back, etc. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element whose order will be changed. Optional - if not provided, will use the currently selected element.",
                },
                position: {
                    type: "string",
                    enum: ["front", "back", "forward", "backward"],
                    description: "The new position for the layer: 'front' (bring to front), 'back' (send to back), 'forward' (bring forward), 'backward' (send backward).",
                }
            },
            required: ["position"],
        },
    },
    {
        name: "resize_element",
        description:
            "Resize a shape or image element. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element to resize. Optional - if not provided, will use the currently selected element.",
                },
                scale: {
                    type: "number",
                    description: "Scale factor to apply to both width and height (e.g., 1.5 for 150%, 0.5 for 50%). Default scale amount is 0.1 (10%) if not specified or scale direction is used.",
                },
                scale_direction: {
                    type: "string",
                    enum: ["up", "down"],
                    description: "Direction to scale - 'up' increases size by 10%, 'down' decreases size by 10%. Alternative to specifying an exact scale factor.",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the resize to all templates. Defaults to false unless requested by the user.",
                }
            },
            required: ["apply_to_all"],
        },
    },
    {
        name: "position_object",
        description:
            "Set the exact X and/or Y coordinates of an object in the canvas. ALWAYS use this function whenever a user refers to 'x' or 'y' without additional context - assume they are referring to position coordinates. This includes ANY command such as 'set x to 100', 'x=50', 'make x 200', 'change x to 75', 'x value of 150', 'y coordinate 300', etc. Whenever you see 'x' or 'y' mentioned with a number, use this function to set the position. In the context of this application, 'x' and 'y' ALWAYS refer to position coordinates unless explicitly stated otherwise. NEVER use move_object for these absolute coordinate requests. If no element_id is specified, the currently selected element will be used.",
        parameters: {
            type: "object",
            properties: {
                element_id: {
                    type: "string",
                    description:
                        "Id of the element to position. Optional - if not provided, will use the currently selected element.",
                },
                x: {
                    type: "number",
                    description: "The exact X coordinate to set for the object's position (horizontal position).",
                },
                y: {
                    type: "number",
                    description: "The exact Y coordinate to set for the object's position (vertical position).",
                },
                apply_to_all: {
                    type: "boolean",
                    description: "Apply the position to all templates. Defaults to false unless requested by the user. Only applies to text and shape elements, not images.",
                }
            },
            // At least one of x or y must be provided, apply_to_all is optional
            required: [],
        },
    },
    {
        name: "resize_banner",
        description:
            "Resize the entire banner to the specified width and height.",
        parameters: {
            type: "object",
            properties: {
                width: {
                    type: "number",
                    description: "The new width of the banner.",
                },
                height: {
                    type: "number",
                    description: "The new height of the banner.",
                }
            },
            required: ["width", "height"],
        },
    },
  {
    name: "switch_template",
    description: "Switch the active/selected template. Use when the user refers to a specific template by ordinal ('first template', 'second template', 'template 1') or by name. After switching, subsequent operations will apply to the newly selected template.",
    parameters: {
      type: "object",
      properties: {
        template_index: {
          type: "integer",
          description: "1-based index of the template to switch to (1 = first, 2 = second, etc.)"
        },
        instance_id: {
          type: "string",
          description: "Alternatively, the instance_id of the template to switch to."
        }
      },
      required: []
    }
  },
  {
    name: "find_element_by_content",
    description: "Find and select an element by its visible text content. Use when the user refers to an element by what it displays (e.g., 'the text 64 oz', 'the text that says CRANBERRY') rather than by its element_id. Returns the matching element.",
    parameters: {
      type: "object",
      properties: {
        text_content: {
          type: "string",
          description: "The text content to search for (case-insensitive partial match)."
        }
      },
      required: ["text_content"]
    }
  },
  {
    name: "generate_image_inline",
    description: "Generate an image using AI directly in the chat, with optional source images from the current template. Use this when the user wants to generate a new image using product images or background images from the template as reference. This works inline without opening the Generate Image popup.",
    parameters: {
      type: "object",
      properties: {
        prompt: {
          type: "string",
          description: "The image generation prompt describing what to create."
        },
        model: {
          type: "string",
          enum: ["gpt_image_1_generate", "gemini_nano_banana_2_image_generate", "gemini_nano_banana_pro_image_generate", "gemini_2_5_flash_image_generate"],
          description: "The AI model to use. Default: gemini_nano_banana_2_image_generate"
        },
        source_element_ids: {
          type: "array",
          items: { type: "string" },
          description: "Optional element IDs of images in the template to use as source/reference images for the generation."
        },
        number_of_images: {
          type: "integer",
          minimum: 1,
          maximum: 4,
          description: "Number of images to generate. Default: 1"
        }
      },
      required: ["prompt"]
    }
  },
  {
    name: "translate_image",
    description: "Translate text within an image to another language. Detects all visible text in the image, then translates and re-renders it while preserving the original layout, colors, and style. Use when the user asks to translate an image to another language.",
    parameters: {
      type: "object",
      properties: {
        element_id: {
          type: "string",
          description: "The image element to translate. Optional - uses the currently selected element if not provided."
        },
        target_language: {
          type: "string",
          description: "The target language for translation. Use the exact language name. Some languages have regional variants: French-CA, French-FR, Spanish (LatAm), Spanish (Spain/EU), Simplified Chinese, Traditional Chinese. If the user just says 'French', 'Spanish', or 'Chinese' without specifying, use the generic name and the system will ask which variant.",
        },
        text_blocks_to_translate: {
          type: "array",
          items: { type: "string" },
          description: "Optional. Specific text blocks to translate. If not provided, all detected text will be translated."
        },
        auto_apply: {
          type: "string",
          enum: ["replace", "new_template"],
          description: "Optional. If the user explicitly says 'replace the current image' or 'add to a new template' in their prompt, set this to skip the confirmation buttons and apply automatically."
        }
      },
      required: ["target_language"]
    }
  },
  {
    name: "duplicate_template",
    description: "Make a copy/duplicate of one template. Creates an exact copy of the currently selected template in the layout, or the template identified by template_index. Do not use this to duplicate an entire layout.",
    parameters: {
      type: "object",
      properties: {
        template_index: {
          type: "integer",
          description: "Optional. 1-based index of the template to duplicate. If not provided, duplicates the currently selected template.",
        },
      },
      required: [],
    },
  },
  {
    name: "duplicate_layout",
    description: "Make a copy/duplicate of the entire current layout, including all of its templates. Use this when the user asks to duplicate the layout, whole layout, or entire layout.",
    parameters: {
      type: "object",
      properties: {},
      required: [],
    },
  },
  {
    name: "add_template_from_design_gallery",
    description: "Add a template from the Design Gallery to the current layout by searching gallery templates and inserting the best-matching template.",
    parameters: {
      type: "object",
      properties: {
        template_query: {
          type: "string",
          description: "Template name or id to match against Design Gallery templates.",
        },
      },
      required: ["template_query"],
    },
  },
  {
    name: "analyze_image_order_template",
    description: "ONLY use for questions about whether the IMAGE SEQUENCE/ORDER within the selected template is good. Trigger on ordering/sequence questions about the current template: 'Are these images in the optimal order?', 'Are these images in the best order?', 'Are these images in the best order for immersive storytelling on a Product Detail Page?', 'Are the images in the selected template in the best order for immersive storytelling on a Product Detail Page?'. Do NOT use for generic 'analyze layout', 'analyze image', or other analysis requests.",
    parameters: {
        type: "object",
        properties: {},
        required: [],
    },
  },
  {
    name: "analyze_image_order_layout",
    description: "ONLY use for questions about whether the IMAGE SEQUENCE/ORDER across all templates is good for storytelling. Trigger phrases must include both an ordering/sequence concept AND a storytelling/PDP concept: 'Are the images in this layout in the best order for immersive storytelling?', 'Are these templates in the best order?'. Do NOT use for generic 'analyze layout', 'analyze image', or other analysis requests.",
    parameters: {
        type: "object",
        properties: {},
        required: [],
    },
  },
  {
    name: "extract_text_from_image",
    description: "Extract text from an image. Use when the user asks to extract text from an image (e.g., 'extract text', 'extract text from image', 'extract text from this image', 'tell me the text in this image', etc.).",
    parameters: {
        type: "object",
        properties: {},
        required: [],
    },
  },
  {
    name: "extract_text_from_user_image",
    description: "Extract all visible text from a user-provided image. Use when the user asks to read, recognize, or extract text from an uploaded image, screenshot, photo, document, or scan.",
    parameters: {
      type: "object",
      properties: {
        text: {
          type: "string",
          description: "The extracted text from the image.",
        },
      },
      required: ["text"],
    },
  },
  {
    "name": "create_template",
    "description": "Returns layout sizes based on campaign (e.g. Walmart Connect) or text input. MANDATORY: If the user asks for an image or to generate an image for a banner ad (e.g. 'Campaign for back to school featuring Lays and Doritos chips'), you MUST provide a visual description in the image_prompt field.",
    "parameters": {
      "type": "object",
      "properties": {
        "sizes": {
          "type": "array",
          "description": "Result sizes",
          "items": {
            "type": "object",
            "properties": {
              "width": { "type": "number" },
              "height": { "type": "number" }
            },
            "required": ["width", "height"]
          }
        },
        "image_prompt": {
          "type": "string",
          "description": "An AI image generation prompt describing a single banner ad that covers the entire image without any paddings/margin/borders. IMPORTANT: The banner ad must contain text and a call to action. Focus on the visual elements, style, and branding. Include specific product names if mentioned (e.g. 'Lays and Doritos'). Do not specify dimensions or aspect ratios in the prompt."
        },
      },
      "required": ["sizes"]
    }
  },
    {
        name: "add_uploaded_images",
        description: "Add one or multiple user-uploaded image attachments to a template/layout in attachment order.",
        parameters: {
            type: "object",
            properties: {
            attachment_indexes: {
                type: "array",
                items: {
                type: "integer"
                },
                description: "0-based uploaded image attachment indexes, in the order they should be added"
            },
            template_index: {
                type: "integer",
                description: "Optional 1-based template index in the layout"
            },
            instance_id: {
                type: "string",
                description: "Optional template instance id to target directly instead of template_index"
            }
            },
            required: ["attachment_indexes"],
            additionalProperties: false
        }
    },
    {
        name: "create_pdp_image_set",
        description:
            "Create PDP (Product Detail Page) images and add them as new template(s) inside the currently-open layout. " +
            "Use when the user wants to create a PDP image, a PDP image set, a hero image, 'how to' image, " +
            "'reason to believe' image, lifestyle image, comparison image, features image, or ingredients image. " +
            "Recognizes prompts like 'Create a PDP image for B0BTMTTXBF', 'Create a PDP image set', " +
            "'Add a hero image for this product', 'Create a how-to image for [product]'. " +
            "ALSO recognizes 'based on' / 'using as reference' / 'in the style of' / 'featuring' prompts that pair " +
            "a target product with one or more reference products or reference images, e.g. " +
            "'Create a PDP image set for B01BOUS7AO based on the images in this product B078TZK3V3', " +
            "'Create images in a clean modern style featuring the product from ASIN B1234566790', " +
            "'Create a PDP image for this product <attached> based on these reference images <attached>'. " +
            "For these reference flows, put the TARGET product in `product_references` and put the " +
            "SOURCE / inspiration products in `reference_product_references`, and set " +
            "`use_attachments_as_reference=true` whenever the user has attached reference (non-target) images. " +
            "Do NOT use for editing an existing element image \u2014 use replace_element_image or edit_element_image instead.",
        parameters: {
            type: "object",
            properties: {
                is_set: {
                    type: "boolean",
                    description:
                        "True when the user asks for multiple images (e.g., 'Create a PDP image set', " +
                        "'Create multiple PDP images', 'Create a set of PDP images', 'Create several images'). " +
                        "False when the user asks for a single image (e.g., 'Create a PDP image', " +
                        "'Create a hero image', 'Make a reason to believe image'). " +
                        "Infer from phrasing: plural nouns / words like 'set', 'multiple', 'several' \u2192 true; " +
                        "singular 'a/an image' with no plural cue \u2192 false.",
                },
                image_types: {
                    type: "array",
                    items: { type: "string" },
                    description:
                        "Types of PDP images requested. Use these exact keys (case-sensitive): " +
                        "'hero', 'mobile_hero' (Mobile Hero), 'mobile_hero_gs1' (Mobile Hero GS1 / GS1 hero), " +
                        "'mobile_hero_cambridge' (Mobile Hero Cambridge / Cambridge hero), " +
                        "'mobile_hero_reference' (Mobile Hero Reference), 'reason_to_believe', " +
                        "'how_to' (how-to), 'lifestyle', 'comparison', 'ingredients', " +
                        "'nutrition_facts' (nutrition facts), 'feature_benefits' (features / feature-benefits), " +
                        "'product_gallery' (product gallery), 'cross_sell' (cross-sell / cross sell), " +
                        "'category_gallery' (category gallery), " +
                        "'feature_iconography_around' (feature & iconography around the product / benefit icons around product), " +
                        "'feature_iconography_bottom' (feature & iconography bottom of image / benefit icons below the image). " +
                        "Match user phrasing case-insensitively: 'Mobile Hero GS1 image' → ['mobile_hero_gs1']; " +
                        "'Cambridge hero' → ['mobile_hero_cambridge']; 'features image' → ['feature_benefits']; " +
                        "'nutrition facts image' → ['nutrition_facts']; " +
                        "'product gallery' → ['product_gallery']; 'cross-sell image' → ['cross_sell']; " +
                        "'category gallery' → ['category_gallery']; " +
                        "'feature and iconography around the product' → ['feature_iconography_around']; " +
                        "'feature and iconography at the bottom' / 'benefit icons below the image' → ['feature_iconography_bottom']. " +
                        "If the user asks for a single generic 'PDP image' without a specific type, leave empty. " +
                        "If the user asks for a 'PDP image set' or 'multiple PDP images', leave empty and the system will suggest a default set.",
                },
                product_references: {
                    type: "array",
                    items: {
                        type: "object",
                        properties: {
                            type: {
                                type: "string",
                                enum: ["gtin", "asin", "name", "attachment", "url"],
                                description:
                                    "'gtin' for numeric product IDs (12-14 digits), 'asin' for Amazon identifiers " +
                                    "(B + 9 alphanumeric), 'name' for brand/product names, 'attachment' for user-attached images, " +
                                    "'url' for an explicit image URL pasted by the user.",
                            },
                            value: { type: "string", description: "The identifier value." },
                            display_name: { type: "string", description: "Human-readable label." },
                        },
                        required: ["type", "value"],
                    },
                    description: "Product identifiers mentioned by the user.",
                },
                background_scene: {
                    type: "string",
                    description: "Background scene description if the user specified one.",
                },
                image_prompt: {
                    type: "string",
                    description:
                        "The full original user prompt for reference. Use to capture descriptive intent like " +
                        "'showing a 3 step process' or specific visual details.",
                },
                reference_product_references: {
                    type: "array",
                    description:
                        "Source / inspiration products to use as REFERENCE images for the generation (scenes, " +
                        "backgrounds, style cues) — NOT the target product. Same shape as product_references. " +
                        "Populate whenever the user says 'based on <product>', 'using <product> as reference', " +
                        "'in the style of <product>', 'featuring the product from <ASIN>', or similar wording " +
                        "that pairs a target product with one or more inspiration products.",
                    items: {
                        type: "object",
                        properties: {
                            type: { type: "string", enum: ["asin", "gtin", "name", "url", "salsify"] },
                            value: { type: "string" },
                            display_name: { type: "string" },
                        },
                        required: ["type", "value"],
                    },
                },
                use_attachments_as_reference: {
                    type: "boolean",
                    description:
                        "Set to true when the user's attached images should be used as REFERENCE images (style, " +
                        "scene, inspiration) rather than as the target product itself. Typical triggers: " +
                        "'based on these images', 'using these as reference', 'in this style', 'matching this look'. " +
                        "Leave false / unset for normal PDP requests where attachments ARE the target product.",
                },
            },
            required: [],
        },
    },
    {
        name: "add_pdp_image_types",
        description:
            "Add more image types to a PDP image set that is ALREADY active or has already been generated in this " +
            "conversation — use this for follow-up requests like 'add Lifestyle and Cross-sell', 'also add a hero " +
            "image', 'include Category Gallery too'. Do NOT use this to start a brand-new PDP image set from " +
            "scratch — use create_pdp_image_set for that.",
        parameters: {
            type: "object",
            properties: {
                image_types: {
                    type: "array",
                    items: { type: "string" },
                    description:
                        "Additional PDP image types to add to the current set. Use these exact keys (case-sensitive): " +
                        "'hero', 'mobile_hero' (Mobile Hero), 'mobile_hero_gs1' (Mobile Hero GS1 / GS1 hero), " +
                        "'mobile_hero_cambridge' (Mobile Hero Cambridge / Cambridge hero), " +
                        "'mobile_hero_reference' (Mobile Hero Reference), 'reason_to_believe', " +
                        "'how_to' (how-to), 'lifestyle', 'ingredients', " +
                        "'nutrition_facts' (nutrition facts), 'feature_benefits' (features / feature-benefits), " +
                        "'product_gallery' (product gallery), 'cross_sell' (cross-sell / cross sell), " +
                        "'category_gallery' (category gallery), " +
                        "'feature_iconography_around' (feature & iconography around the product / benefit icons around product), " +
                        "'feature_iconography_bottom' (feature & iconography bottom of image / benefit icons below the image). " +
                        "Match user phrasing case-insensitively: 'Cambridge hero' → ['mobile_hero_cambridge']; " +
                        "'features image' → ['feature_benefits']; 'cross-sell' → ['cross_sell'].",
                },
            },
            required: ["image_types"],
            additionalProperties: false,
        },
    }
];
